<template>
    <DCard padding>
        <DCol>
            <DRow class="mb-3">
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DDateFormatInput
                        id="date"
                        v-model="dataFilters.date"
                        format="YYYY-MM-DD"
                        type="date"
                        :placeholder="__('tools.partners.compensation_rates.valid_from')"
                        :label="__('tools.dunning.filters.date')"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage(dunningLetters.current_page)"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DSelect
                        id="type-dropdown"
                        v-model="dataFilters.type"
                        :label="__('tools.dunning.filters.type')"
                        :options="dunningTypesMapped"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DSelect
                        id="brand-dropdown"
                        v-model="dataFilters.brand"
                        :label="__('tools.dunning.filters.brand')"
                        :options="dunningBrandsMapped"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DSelect
                        id="payment-status-dropdown"
                        v-model="dataFilters.paymentStatus"
                        :label="__('tools.dunning.filters.paymentstatus')"
                        :options="dunningPaymentstatus"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <label class="form-label">{{ __('tools.dunning.filters.customersegments') }}</label>
                    <DMultiSelect
                        id="customer-segments-dropdown"
                        :options="dunningCustomerSegmentsOptions"
                        selection-type="text"
                        :selected="selectedCustomerSegments"
                        :selection="true"
                        :search="true"
                        options-empty-placeholder="No options placeholder"
                        @update="(values) => onCustomersegmentsFilterUpdate(values)"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="customer-id"
                        v-model="dataFilters.customerId"
                        :label="__('tools.dunning.filters.customer_id')"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage(dunningLetters.current_page)"
                    />
                </DCol>
                <DCol
                    sm="2"
                    class="pt-1"
                >
                    <DInput
                        id="invoice-id"
                        v-model="dataFilters.invoiceId"
                        :label="__('tools.dunning.filters.invoice_id')"
                        class="mb-0"
                        @keydown.enter.exact.prevent="changePage(dunningLetters.current_page)"
                    />
                </DCol>
            </DRow>
            <DRow class="mb-3">
                <DCol
                    sm="3"
                    class="d-flex gap-2"
                >
                    <DButton
                        id="filter-apply"
                        :label="__('tools.dunning.filters.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        icon="filter-off"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
        </DCol>
        <DTable
            :items="dunningLetters.data"
            :fields="fields"
            hover
        >
            <template #type="{item}">
                <td>
                    <DBadge
                        :color="'danger'"
                        shape="rounded"
                    >
                        {{ __(`tools.dunning.types.${item.type}`) }}
                    </DBadge>
                </td>
            </template>
            <template #invoiceId="{item}">
                <td v-if="userPermissions.includes('invoice.view')">
                    <Link :href="$route('customers.customers.invoices.show', {customer: item.customerId, invoice: item.invoiceId})">
                        {{ item.invoiceId }}
                    </Link>
                </td>
                <td v-else>
                    {{ item.invoiceId }}
                </td>
            </template>
            <template #customer="{item}">
                <td v-if="userPermissions.includes('customer.view')">
                    <Link :href="$route('customers.customers.show', {customer: item.customerId})">
                        {{ customerName(item) }}
                    </Link>
                </td>
                <td v-else>
                    {{ customerName(item) }}
                </td>
            </template>
            <template #dunningStop="{item}">
                <td v-if="item.dunningStop">
                    {{ item.dunningStop }}
                </td>
                <td v-else />
            </template>
            <template #invoiceAmount="{item}">
                <td
                    class="text-right"
                    :class="{'table-text-color-red': item.invoiceAmount < 0}"
                >
                    {{ priceFormatForDisplay(item.invoiceAmount, 2, item.marketRegionId) }}
                </td>
            </template>
            <template #openItemAmount="{item}">
                <td
                    class="text-right"
                    :class="{'table-text-color-red': item.openItemAmount < 0}"
                >
                    {{ priceFormatForDisplay(item.openItemAmount, 2, item.marketRegionId) }}
                </td>
            </template>
            <template #date="{item}">
                <td>
                    {{ setDateFormat(item.date) }}
                </td>
            </template>
            <template #sanity="{item}">
                <td>{{ __('customers.common.sanity.' + item.sanity) }}</td>
            </template>
            <template #invoiceStatus="{item}">
                <td>
                    <DBadge
                        :color="statusColor(item)"
                        shape="rounded"
                    >
                        {{ __(`tools.dunning.invoice_status.${item.invoiceStatus}`) }}
                    </DBadge>
                    {{ }}
                </td>
            </template>
            <template #actions="{item}">
                <td>
                    <div style="display: flex; grid-gap: 10px;">
                        <DButton
                            icon="download"
                            mini
                            :disabled="isInLoadingState"
                            @click="download(item)"
                        />
                    </div>
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="dunningLetters.last_page > 1"
            class="pb-2"
            :active-page="dunningLetters.current_page"
            :pages="dunningLetters.last_page"
            align="center"
            @update:activePage="changePage"
        />
    </DCard>
</template>

<script>

import _ from 'lodash'
import priceFormat from '~@/Mixins/priceFormat'

export default {
    name: 'DunningIndex',
    mixins: [
        priceFormat
    ],
    props: {
        dunningLetters: {
            type: Object,
            default: () => { return {} }
        },
        dunningTypes: {
            type: Array,
            default: () => { return [] }
        },
        dunningBrands: {
            type: Array,
            default: () => { return [] }
        },
        dunningPaymentstatus: {
            type: Array,
            default: () => { return [] }
        },
        dunningCustomersegments: {
            type: Array,
            default: () => { return [] }
        },
        filters: {
            type: Object,
            default: () => {
                return {
                    date: null,
                    type: null,
                    brand: null,
                    paymentStatus: null,
                    customerSegments: [],
                    customerId: null,
                    invoiceId: null
                }
            }
        }
    },
    data () {
        return {
            fields: [
                { key: 'id', label: this.__('tools.dunning.table.id') },
                { key: 'date', label: this.__('tools.dunning.table.date') },
                { key: 'type', label: this.__('tools.dunning.table.type') },
                { key: 'invoiceId', label: this.__('tools.dunning.table.invoice_id') },
                { key: 'customer', label: this.__('tools.dunning.table.customer') },
                { key: 'dunningStop', label: this.__('tools.dunning.table.dunning_stop') },
                { key: 'invoiceAmount', label: this.__('tools.dunning.table.invoice_amount') },
                { key: 'openItemAmount', label: this.__('tools.dunning.table.open_item_amount') },
                { key: 'invoiceStatus', label: this.__('tools.dunning.table.invoice_status') },
                { key: 'sanity', label: this.__('tools.dunning.table.sanity') },
                { key: 'actions', label: this.__('tools.dunning.table.actions') }
            ],
            dataFilters: _.cloneDeep(this.filters)
        }
    },
    computed: {
        dunningTypesMapped () {
            const mappedTypes = this.dunningTypes.map(dunningType => {
                return {
                    value: dunningType.type,
                    label: this.__(`tools.dunning.types.${dunningType.type}`)
                }
            })

            mappedTypes.unshift({
                value: '',
                label: ''
            })

            return mappedTypes
        },
        dunningBrandsMapped () {
            const mappedTypes = this.dunningBrands.map(dunningBrand => {
                return {
                    value: dunningBrand.id,
                    label: dunningBrand.name
                }
            })

            mappedTypes.unshift({
                value: '',
                label: ''
            })

            return mappedTypes
        },
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        selectedCustomerSegments () {
            return this.dataFilters.customerSegments
        },
        dunningCustomerSegmentsOptions () {
            return this.dunningCustomersegments.map((option) => ({
                ...option,
                selected: this.filters.customerSegments.includes(option.value)
            }))
        }
    },
    methods: {
        changePage (page) {
            const queryParameters = Object.fromEntries(Object.entries(this.dataFilters).filter(([_, v]) => v))

            if (page !== 1) {
                queryParameters.page = page
            }

            this.$inertia.get(this.$route('tools.dunning.index', {
                saveFilters: true
            }), queryParameters)
        },
        customerName (item) {
            const name = item.company || `${item.firstName} ${item.lastName}`
            return name + ` (${item.customerId})`
        },
        statusColor (item) {
            switch (item.invoiceStatus) {
                case 'paid':
                    return 'success'
                case 'payment_pending':
                case 'unpaid':
                    return 'warning'
            }
        },
        download (item) {
            const link = document.createElement('a')
            link.href = this.$route('tools.dunning.download', { dunning: item.id })
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },
        resetFilters () {
            this.dataFilters.date = null
            this.dataFilters.type = null
            this.dataFilters.brand = null
            this.dataFilters.paymentStatus = null
            this.dataFilters.customerSegments = []
            this.dataFilters.customerId = null
            this.dataFilters.invoiceId = null
            const queryParameters = Object.fromEntries(Object.entries(this.dataFilters).filter(([_, v]) => v))
            queryParameters.page = 1
            this.$inertia.get(this.$route('tools.dunning.index', {
                saveFilters: true,
                defaultFilters: true
            }), queryParameters)
        },
        onCustomersegmentsFilterUpdate (value) {
            this.dataFilters.customerSegments = value || null
        }
    }
}
</script>
